import L from "leaflet";
import "leaflet/dist/leaflet.css";
import React, { useEffect, useState } from "react";
import { MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from "react-leaflet";
import { useParams } from "react-router-dom";
import dropIconImg from '../../assets/images/dropIcon.svg';
import pickupIconImg from '../../assets/images/pickupIcon.svg';
import riderIconImg from '../../assets/images/rider_icon.png';
import { HYDRA_API_CALL } from "../../common/services/apiConfig";
import { defaultHeader } from "../../common/services/apiUtils";
import { decryptToken, generateChecksum } from "../../utils/crypto.utils";

// Helper to parse float safely
function safeFloat(val) {
    if (typeof val === "string") return parseFloat(val);
    return val;
}

const riderIcon = L.icon({
    iconUrl: riderIconImg,
    iconSize: [48, 48],
    iconAnchor: [24, 24],
    popupAnchor: [0, -24],
    zIndexOffset: 10000, // Ensure rider marker is always on top
});

const pickupIcon = L.icon({
    iconUrl: pickupIconImg,
    iconSize: [48, 48],
    iconAnchor: [24, 24],
    popupAnchor: [0, -24],
    zIndexOffset: 10000, // Ensure rider marker is always on top
});

const dropIcon = L.icon({
    iconUrl: dropIconImg,
    iconSize: [48, 48],
    iconAnchor: [24, 24],
    popupAnchor: [0, -24],
    zIndexOffset: 10000, // Ensure rider marker is always on top
});

// Component to center map on given coordinates
function CenterMapToRider({ center }) {
    const map = useMap();
    useEffect(() => {
        if (center && center[0] && center[1]) {
            map.setView(center);
        }
    }, [center, map]);
    return null;
}

export default function EmbedOrderTracking() {
    
    const { token, orderID } = useParams();
    
    let decryptedToken = "";
    try {
        decryptedToken = decryptToken(token);
    } catch (e) {
        decryptedToken = "Invalid or undecryptable token";
    }

    console.log(decryptedToken);

    const [riderData, setRiderData] = useState([]);
    const [mapCenter, setMapCenter] = useState([20.5937, 78.9629]); // Default: India center
    const [riderLocForCenter, setRiderLocForCenter] = useState(null);
    const [routes, setRoutes] = useState([]); // Array of arrays of latlngs

    useEffect(() => {
        const firstUnderscore = decryptedToken.indexOf("_");
        let tokenId = "";
        let organisationCode = "";
        if (firstUnderscore !== -1) {
            tokenId = decryptedToken.substring(0, firstUnderscore);
            organisationCode = decryptedToken.substring(firstUnderscore + 1);
        }
        const checksum = generateChecksum(tokenId, organisationCode);
        const postObject = {
            organisationCode,
            encodedWaybillNos: [orderID]
        };
        HYDRA_API_CALL("POST", "/lm/rider-details", JSON.stringify(postObject), [...defaultHeader, ["checksum", checksum]])
            .then(async response => {
                if (Array.isArray(response) && response.length > 0) {
                    setRiderData(response);
                    // Center map on rider if available
                    const riderLoc = response[0]?.rider?.location;
                    if (riderLoc) {
                        setRiderLocForCenter([
                            safeFloat(riderLoc.latitude),
                            safeFloat(riderLoc.longitude)
                        ]);
                    }
                    // Fetch and set routes for each consignment
                    const routesArr = await Promise.all(response.map(async item => {
                        const pickupLoc = item.consignment?.pickupLocation;
                        const riderLoc = item.rider?.location;
                        const deliveryLoc = item.consignment?.deliveryLocation;
                        const routePoints = [];
                        // Pickup -> Rider
                        if (pickupLoc && riderLoc) {
                            const coords = `${safeFloat(pickupLoc.longitude)},${safeFloat(pickupLoc.latitude)};${safeFloat(riderLoc.longitude)},${safeFloat(riderLoc.latitude)}`;
                            const url = `https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`;
                            try {
                                const res = await fetch(url);
                                const data = await res.json();
                                if (data.routes && data.routes[0]) {
                                    routePoints.push(...data.routes[0].geometry.coordinates.map(([lng, lat]) => [lat, lng]));
                                }
                            } catch (e) { /* ignore */ }
                        }
                        // Rider -> Delivery
                        if (riderLoc && deliveryLoc) {
                            const coords = `${safeFloat(riderLoc.longitude)},${safeFloat(riderLoc.latitude)};${safeFloat(deliveryLoc.longitude)},${safeFloat(deliveryLoc.latitude)}`;
                            const url = `https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`;
                            try {
                                const res = await fetch(url);
                                const data = await res.json();
                                if (data.routes && data.routes[0]) {
                                    // Avoid duplicating the rider point
                                    const segment = data.routes[0].geometry.coordinates.map(([lng, lat]) => [lat, lng]);
                                    if (routePoints.length && segment.length) segment.shift();
                                    routePoints.push(...segment);
                                }
                            } catch (e) { /* ignore */ }
                        }
                        return routePoints;
                    }));
                    setRoutes(routesArr);
                }
            })
            .catch(error => {
                console.error("Error fetching rider details:", error);
            });
    }, [decryptedToken, orderID]);

    return (
        <div style={{ height: "500px", width: "100%", marginBottom: -10 }}>
            <MapContainer center={mapCenter}
                zoom={6} style={{ height: "100%", width: "100%" }}>
                    <CenterMapToRider center={riderLocForCenter} />
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution="&copy; OpenStreetMap contributors" />
                {riderData.map((item, idx) => {
                    const riderLoc = item.rider?.location;
                    const pickupLoc = item.consignment?.pickupLocation;
                    const deliveryLoc = item.consignment?.deliveryLocation;
                    const points = [];
                    if (pickupLoc) points.push([safeFloat(pickupLoc.latitude), safeFloat(pickupLoc.longitude)]);
                    if (riderLoc) points.push([safeFloat(riderLoc.latitude), safeFloat(riderLoc.longitude)]);
                    if (deliveryLoc) points.push([safeFloat(deliveryLoc.latitude), safeFloat(deliveryLoc.longitude)]);
                    return (
                        <React.Fragment key={idx}>
                            {/* Rider Marker */}
                            {riderLoc && (
                                <Marker position={[safeFloat(riderLoc.latitude), safeFloat(riderLoc.longitude)]}
                                    icon={riderIcon}>
                                    <Popup>
                                        <b>Rider:</b> {item.rider.name}<br />
                                        <b>Contact:</b> {item.rider.maskedContactNumber}<br />
                                        <b>Hub:</b> {item.rider.hubName}<br />
                                        <b>Last Updated:</b> {new Date(item.rider.location.refreshedAt).toLocaleString()}
                                    </Popup>
                                </Marker>
                            )}
                            {/* Pickup Marker (Red) */}
                            {pickupLoc && (
                                <Marker 
                                    position={[safeFloat(pickupLoc.latitude), safeFloat(pickupLoc.longitude)]}
                                    icon={pickupIcon}
                                >
                                    <Popup>
                                        <b>Pickup Location</b>
                                    </Popup>
                                </Marker>
                            )}
                            {/* Delivery Marker (Green) */}
                            {deliveryLoc && (
                                <Marker 
                                    position={[safeFloat(deliveryLoc.latitude), safeFloat(deliveryLoc.longitude)]}
                                    icon={dropIcon}
                                >
                                    <Popup>
                                        <b>Delivery Location</b>
                                    </Popup>
                                </Marker>
                            )}
                                {/* Polyline: Pickup -> Rider -> Delivery (actual route) */}
                                {routes[idx] && routes[idx].length > 1 && (
                                    <Polyline positions={routes[idx]} color="blue" />
                                )}
                        </React.Fragment>
                    );
                })}
            </MapContainer>
        </div>
    );
}
